import { MAX_POST_BODY_LENGTH, type PostSummary } from '@fetmode/types';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';
import { formatUsername } from '@fetmode/utils';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../auth/index.js';
import {
  COMPACT_GRID_PAGE_SIZE,
  ThumbGridPager,
  usePagedItems,
} from '../../shared/components/ThumbGridPager/index.js';
import './ProfilePostsPanel.css';

function initialsFor(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  const chars = parts.length > 1 ? `${parts[0]![0]}${parts[1]![0]}` : trimmed.slice(0, 2);
  return chars.toUpperCase();
}

function Avatar({
  url,
  name,
  size,
}: {
  url: string | null;
  name: string;
  size: number;
}): ReactElement {
  if (url) {
    return (
      <img
        className="profile-posts__avatar"
        src={url}
        alt=""
        width={size}
        height={size}
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      className="profile-posts__avatar profile-posts__avatar--initials"
      aria-hidden="true"
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {initialsFor(name)}
    </span>
  );
}

function formatPostTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export interface ProfilePostsPanelProps {
  username: string;
  isSelf: boolean;
  onCountChange?: (delta: number) => void;
}

export function ProfilePostsPanel({
  username,
  isSelf,
  onCountChange,
}: ProfilePostsPanelProps): ReactElement {
  const { api } = useAuth();
  const [posts, setPosts] = useState<PostSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [body, setBody] = useState('');
  const [posting, setPosting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const postsPaging = usePagedItems(posts, COMPACT_GRID_PAGE_SIZE);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    void api
      .listUserPosts(username)
      .then((result) => {
        if (active) setPosts(result.posts);
      })
      .catch(() => {
        if (active) setLoadError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api, username]);

  const handleSubmit = (): void => {
    const trimmed = body.trim();
    if (!trimmed || posting) return;
    setPosting(true);
    setActionError(null);
    void api
      .createPost({ body: trimmed })
      .then((result) => {
        setPosts((prev) => [result.post, ...prev]);
        setBody('');
        onCountChange?.(1);
      })
      .catch(() => setActionError(t('common.profile.posts.createError')))
      .finally(() => setPosting(false));
  };

  const handleDelete = (postId: string): void => {
    setActionError(null);
    void api
      .deletePost(postId)
      .then(() => {
        setPosts((prev) => prev.filter((post) => post.id !== postId));
        onCountChange?.(-1);
      })
      .catch(() => setActionError(t('common.profile.posts.deleteError')));
  };

  return (
    <section className="profile-posts" aria-labelledby="profile-posts-heading">
      <h2 id="profile-posts-heading" className="fm-sr-only">
        {t('common.profile.posts.title')}
      </h2>

      {isSelf ? (
        <form
          className="profile-posts__composer"
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmit();
          }}
        >
          <label className="fm-sr-only" htmlFor="profile-post-body">
            {t('common.profile.posts.composerLabel')}
          </label>
          <textarea
            id="profile-post-body"
            className="profile-posts__textarea"
            placeholder={t('common.profile.posts.placeholder')}
            value={body}
            maxLength={MAX_POST_BODY_LENGTH}
            rows={3}
            onChange={(event) => setBody(event.target.value)}
          />
          <div className="profile-posts__composer-foot">
            <span className="profile-posts__char-count">
              {body.length}/{MAX_POST_BODY_LENGTH}
            </span>
            <Button
              label={posting ? t('common.profile.posts.posting') : t('common.profile.posts.submit')}
              variant="primary"
              type="submit"
              disabled={posting || body.trim().length === 0}
            />
          </div>
        </form>
      ) : null}

      {actionError ? <p className="fm-error">{actionError}</p> : null}

      {loading ? (
        <p className="profile-posts__muted">{t('common.profile.posts.loading')}</p>
      ) : loadError ? (
        <p className="fm-error">{t('common.profile.posts.loadError')}</p>
      ) : posts.length === 0 ? (
        <p className="profile-posts__muted">
          {isSelf ? t('common.profile.posts.emptySelf') : t('common.profile.posts.emptyOther')}
        </p>
      ) : (
        <ul className="profile-posts__list">
          {postsPaging.pageItems.map((post) => (
            <li key={post.id} className="profile-posts__item">
              <Avatar url={post.author.avatarUrl} name={post.author.displayName} size={44} />
              <div className="profile-posts__item-body">
                <div className="profile-posts__item-head">
                  <Link to={`/@${post.author.username}`} className="profile-posts__item-author">
                    <strong>{post.author.displayName}</strong>
                    <span>{formatUsername(post.author.username)}</span>
                  </Link>
                  <time className="profile-posts__item-time" dateTime={post.createdAt}>
                    {formatPostTime(post.createdAt)}
                  </time>
                </div>
                <p className="profile-posts__item-text">{post.body}</p>
                {post.isOwn ? (
                  <button
                    type="button"
                    className="profile-posts__delete"
                    onClick={() => handleDelete(post.id)}
                  >
                    {t('common.profile.posts.delete')}
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
      <ThumbGridPager
        page={postsPaging.page}
        total={postsPaging.total}
        pageSize={COMPACT_GRID_PAGE_SIZE}
        label={t('common.profile.posts.title')}
        onPageChange={postsPaging.setPage}
      />
    </section>
  );
}
